import type { SystemStyleObject } from "@chakra-ui/react"
import { Box, Button } from "@chakra-ui/react"
import type { Extensions } from "@tiptap/core"
import Color from "@tiptap/extension-color"
import Highlight from "@tiptap/extension-highlight"
import Image from "@tiptap/extension-image"
import LinkExtension from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import SubscriptExtension from "@tiptap/extension-subscript"
import SuperscriptExtension from "@tiptap/extension-superscript"
import { TextStyle } from "@tiptap/extension-text-style"
import Underline from "@tiptap/extension-underline"
import { useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react"
import { PATHICON_LIST_STYLES } from "../pathicon/richTextStyles"
import { RichTextEditor as ChakraRichTextEditor } from "./chakra/rich-text-editor"
import { PathiconListItem } from "./pathicons/pathicon-list-item-extension"
import { RichTextEditorBubbleToolbar } from "./toolbar/BubbleToolbar"
import { EmbedComposer } from "./toolbar/EmbedComposer"
import { editorContentHTMLTextStyles } from "./toolbar/editor-prose-styles"
import { EmbedIframe } from "./toolbar/embed-iframe-extension"
import {
  insertAttachmentFromMedia,
  pickAndInsertAttachmentFromFilestack
} from "./toolbar/insertion/attachment-insert"
import {
  insertImageFromMedia,
  pickAndInsertImageFromFilestack
} from "./toolbar/insertion/image-insert"
import { SlashInsertionExtension } from "./toolbar/slash/slash-insertion-extension"
import {
  buildSlashInsertionItems,
  type SlashInsertionActions
} from "./toolbar/slash/slash-insertion-items"
import {
  DEFAULT_TOOLBAR_OPTIONS,
  isInsertionToolbarOption,
  normalizeToolbarOption,
  type EditorChain,
  type InsertionToolbarOption
} from "./toolbar/types"
import { useEmbedComposer } from "./toolbar/useEmbedComposer"
import type {
  RichTextEditorEditorOptions,
  RichTextEditorPickedMedia,
  RichTextEditorUploadRenderer
} from "./types"

export function RichTextEditorEditMode({
  value,
  onChange,
  controls,
  editorStyles,
  editor: editorOptions,
  enableSlashMenu = false,
  autoFocus = false,
  disabled = false,
  renderUploadImage,
  renderUploadFile
}: {
  value: string
  onChange: (value: string) => void
  controls?: ReactNode
  editorStyles?: SystemStyleObject
  editor?: RichTextEditorEditorOptions
  enableSlashMenu?: boolean
  autoFocus?: boolean
  disabled?: boolean
  renderUploadImage?: RichTextEditorUploadRenderer
  renderUploadFile?: RichTextEditorUploadRenderer
}) {
  const cfg = editorOptions?.config
  const onChangeRef = useRef(onChange)
  const initialContentRef = useRef(value)
  const lastEmittedHTMLRef = useRef(value)

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const placeholder =
    editorOptions?.placeholder ||
    cfg?.placeholder ||
    cfg?.payload?.placeholder ||
    "Start typing..."

  const toolbarOptions = useMemo(() => {
    const options =
      editorOptions?.toolbarOptions ||
      cfg?.toolbar?.options ||
      cfg?.toolbarOptions ||
      cfg?.payload?.toolbar?.options ||
      cfg?.payload?.toolbarOptions ||
      DEFAULT_TOOLBAR_OPTIONS
    const normalized = options.map(normalizeToolbarOption)
    if (!enableSlashMenu) {
      return normalized.filter((option) => !isInsertionToolbarOption(option))
    }
    return normalized
  }, [editorOptions, cfg, enableSlashMenu])

  const bubbleToolbarOptions = useMemo(
    () => toolbarOptions.filter((option) => !isInsertionToolbarOption(option)),
    [toolbarOptions]
  )

  const insertionToolbarOptions = useMemo(() => {
    const seen = new Set<InsertionToolbarOption>()
    return toolbarOptions.filter(isInsertionToolbarOption).filter((option) => {
      if (seen.has(option)) return false
      seen.add(option)
      return true
    })
  }, [toolbarOptions])

  const hiddenImageTriggerRef = useRef<HTMLButtonElement | null>(null)
  const hiddenAttachmentTriggerRef = useRef<HTMLButtonElement | null>(null)
  const editModeRootRef = useRef<HTMLDivElement | null>(null)

  const slashActionsRef = useRef<SlashInsertionActions>({
    insertionOptions: [],
    triggerImage: () => {},
    triggerAttachment: () => {},
    openEmbedComposer: () => {}
  })
  const embedComposerOpenRef = useRef(false)

  slashActionsRef.current.insertionOptions = insertionToolbarOptions

  const getSlashItems = useCallback(
    (query: string) => buildSlashInsertionItems(query, slashActionsRef.current),
    []
  )

  const shouldShowSlash = useCallback(
    () =>
      enableSlashMenu &&
      !embedComposerOpenRef.current &&
      insertionToolbarOptions.length > 0,
    [enableSlashMenu, insertionToolbarOptions]
  )

  const extensions = useMemo((): Extensions => {
    const baseExtensions: Extensions = [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6]
        },
        listItem: false
      }),
      PathiconListItem,
      Underline,
      LinkExtension.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https"
      }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      SubscriptExtension,
      SuperscriptExtension,
      Image.configure({
        inline: true
      }),
      Placeholder.configure({
        placeholder
      }),
      EmbedIframe
    ]

    if (!enableSlashMenu) return baseExtensions

    return [
      ...baseExtensions,
      SlashInsertionExtension.configure({
        getItems: getSlashItems,
        shouldShow: shouldShowSlash
      })
    ]
  }, [enableSlashMenu, getSlashItems, placeholder, shouldShowSlash])

  const editor = useEditor(
    {
      content: initialContentRef.current,
      extensions,
      editable: true,
      onUpdate: ({ editor: ed }) => {
        const html = ed.getHTML()
        lastEmittedHTMLRef.current = html
        onChangeRef.current(html)
      },
      shouldRerenderOnTransaction: false,
      immediatelyRender: false
    },
    [extensions]
  )

  const insertImageFromMediaCb = useCallback(
    (file: RichTextEditorPickedMedia) => {
      if (!editor) return
      insertImageFromMedia(editor, file, (e) => e.chain() as EditorChain)
    },
    [editor]
  )

  const insertAttachmentFromMediaCb = useCallback(
    (file: RichTextEditorPickedMedia) => {
      if (!editor) return
      insertAttachmentFromMedia(editor, file)
    },
    [editor]
  )

  const handleInsertImage = useCallback(async () => {
    if (!editor) return
    await pickAndInsertImageFromFilestack(editor, insertImageFromMediaCb)
  }, [editor, insertImageFromMediaCb])

  const handleInsertAttachment = useCallback(async () => {
    if (!editor) return
    await pickAndInsertAttachmentFromFilestack(
      editor,
      insertAttachmentFromMediaCb
    )
  }, [editor, insertAttachmentFromMediaCb])

  const embed = useEmbedComposer(editor)

  embedComposerOpenRef.current = embed.embedComposerOpen

  const getInsertionHandler = useCallback(
    (option: InsertionToolbarOption): (() => void) => {
      switch (option) {
        case "image":
          if (renderUploadImage) {
            return () => hiddenImageTriggerRef.current?.click()
          }
          return handleInsertImage
        case "attachment":
          if (renderUploadFile) {
            return () => hiddenAttachmentTriggerRef.current?.click()
          }
          return handleInsertAttachment
        case "embed":
          return embed.openEmbedComposer
      }
    },
    [
      renderUploadImage,
      renderUploadFile,
      handleInsertAttachment,
      embed.openEmbedComposer,
      handleInsertImage
    ]
  )

  useEffect(() => {
    if (!editor) return
    slashActionsRef.current = {
      insertionOptions: insertionToolbarOptions,
      triggerImage: () => getInsertionHandler("image")(),
      triggerAttachment: () => getInsertionHandler("attachment")(),
      openEmbedComposer: embed.openEmbedComposer
    }
  }, [
    editor,
    embed.openEmbedComposer,
    getInsertionHandler,
    insertionToolbarOptions
  ])

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    const editorHtml = editor.getHTML()
    if (value === editorHtml) {
      lastEmittedHTMLRef.current = value
      return
    }
    editor.commands.setContent(value, { emitUpdate: false })
    lastEmittedHTMLRef.current = value
  }, [editor, value])

  useEffect(() => {
    if (autoFocus && editor) editor.commands.focus()
  }, [autoFocus, !!editor])

  if (!editor) return null

  return (
    <Box pos="relative" ref={editModeRootRef}>
      {controls}
      <ChakraRichTextEditor.Root
        editor={editor}
        disabled={disabled}
        css={[
          editorContentHTMLTextStyles,
          editorStyles || editorOptions?.css,
          PATHICON_LIST_STYLES
        ]}
      >
        {enableSlashMenu && renderUploadImage
          ? renderUploadImage({
              renderTrigger: (onClick) => (
                <Button
                  type="button"
                  ref={hiddenImageTriggerRef}
                  onClick={onClick}
                  tabIndex={-1}
                  aria-hidden="true"
                  h="0"
                  w="0"
                  minW="0"
                  minH="0"
                  p="0"
                  m="0"
                  borderWidth="0"
                  opacity={0}
                  pointerEvents="none"
                />
              ),
              insert: insertImageFromMediaCb
            })
          : null}
        {enableSlashMenu && renderUploadFile
          ? renderUploadFile({
              renderTrigger: (onClick) => (
                <Button
                  type="button"
                  ref={hiddenAttachmentTriggerRef}
                  onClick={onClick}
                  tabIndex={-1}
                  aria-hidden="true"
                  h="0"
                  w="0"
                  minW="0"
                  minH="0"
                  p="0"
                  m="0"
                  borderWidth="0"
                  opacity={0}
                  pointerEvents="none"
                />
              ),
              insert: insertAttachmentFromMediaCb
            })
          : null}
        <RichTextEditorBubbleToolbar
          editor={editor}
          bubbleToolbarOptions={bubbleToolbarOptions}
        />
        {enableSlashMenu && embed.embedComposerOpen ? (
          <EmbedComposer
            embedComposerRef={embed.embedComposerRef}
            embedTextareaRef={embed.embedTextareaRef}
            embedInputValue={embed.embedInputValue}
            embedInputError={embed.embedInputError}
            onChangeValue={embed.setEmbedInputValue}
            onClearError={() => embed.setEmbedInputError(null)}
            onSubmit={embed.handleSubmitEmbed}
            onCancel={embed.handleCancelEmbed}
          />
        ) : null}
        <ChakraRichTextEditor.Content />
      </ChakraRichTextEditor.Root>
    </Box>
  )
}

export default RichTextEditorEditMode
